import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Scale, 
  Shield, 
  Building2, 
  Target, 
  Share2, 
  Database, 
  Lock, 
  FileText, 
  CheckCircle2,
  ArrowRight 
} from 'lucide-react';
import Section from '../components/Section';

const KVKK: React.FC = () => {
  // Animasyon Varyantları
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <>
      {/* 1. HERO SECTION */}
      <section className="relative pt-32 pb-20 bg-primary-900 overflow-hidden">
        {/* Arka plan efektleri */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
            <div className="absolute top-[-10%] right-[20%] w-96 h-96 bg-indigo-600 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-72 h-72 bg-blue-600 rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/5 rounded-3xl mb-6 text-primary-300 border border-white/10 backdrop-blur-sm shadow-2xl">
               <Scale size={40} />
            </div>
            <h1 className="text-3xl md:text-5xl font-display font-bold mb-6 text-white tracking-tight">
              KVKK Aydınlatma Metni
            </h1>
            <p className="text-lg md:text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Kişisel verilerinizin güvenliği ve gizliliği konusundaki hassasiyetimizle, <br className="hidden md:block" />
              6698 sayılı Kanun kapsamındaki aydınlatma yükümlülüğümüzü yerine getiriyoruz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. CONTENT SECTION */}
      <Section className="py-16 -mt-12 relative z-20">
        <motion.div 
          className="max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl shadow-gray-900/10 border border-gray-100 overflow-hidden"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          
          {/* GİRİŞ VE PRENSİPLER */}
          <div className="p-8 md:p-12 border-b border-gray-100">
             <div className="flex items-center gap-4 mb-6">
                <div className="bg-primary-50 p-3 rounded-xl text-primary-600">
                    <Shield size={28} />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Veri Sorumlusunun Aydınlatma Yükümlülüğü</h2>
             </div>
             
             <div className="text-gray-600 leading-relaxed space-y-4">
                <p>
                  Kişisel verilerin işlenmesinde başta Anayasa’nın 20. maddesi olmak üzere kişilerin temel hak ve özgürlüklerini korumayı amaçlayan <strong>6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK")</strong> uyarınca; 
                  <strong> LOTUS SPORT TEKNOLOJİ VE EĞİTİM HİZMETLERİ SANAYİ PAZARLAMA VE TİCARET LİMİTED ŞİRKETİ</strong> olarak, veri sorumlusu sıfatıyla kişisel verilerinizi aşağıda izah edildiği surette ve mevzuat tarafından emredilen sınırlar çerçevesinde işlemekteyiz.
                </p>
                
                <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 mt-6">
                    <h4 className="font-bold text-gray-900 mb-4">Genel İşleme İlkelerimiz (KVKK Md. 4)</h4>
                    <ul className="grid md:grid-cols-2 gap-3">
                        {[
                            "Hukuka ve dürüstlük kurallarına uygunluk",
                            "Doğru ve gerektiğinde güncel olma",
                            "Belirli, açık ve meşru amaçlar için işlenme",
                            "İşlendikleri amaçla bağlantılı, sınırlı ve ölçülü olma",
                            "İlgili mevzuatta öngörülen süre kadar muhafaza edilme"
                        ].map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                <CheckCircle2 size={16} className="text-green-500 mt-0.5 shrink-0" />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
             </div>
          </div>

          {/* MADDELER (A, B, C...) */}
          <div className="bg-gray-50/30">
              
              {/* A) Veri Sorumlusu */}
              <div className="p-8 md:p-12 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <div className="flex gap-4">
                      <div className="bg-blue-100 text-blue-600 w-10 h-10 rounded-lg flex items-center justify-center shrink-0 font-bold">A</div>
                      <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <Building2 size={20} className="text-gray-400"/> Veri Sorumlusu
                          </h3>
                          <p className="text-gray-600 leading-relaxed">
                            Kişisel verileriniz, veri sorumlusu sıfatıyla <strong>LOTUS SPORT TEKNOLOJİ VE EĞİTİM HİZMETLERİ SANAYİ PAZARLAMA VE TİCARET LİMİTED ŞİRKETİ</strong> tarafından 6698 sayılı Kanun uyarınca işlenmektedir.
                          </p>
                      </div>
                  </div>
              </div>

              {/* B) İşleme Amaçları */}
              <div className="p-8 md:p-12 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <div className="flex gap-4">
                      <div className="bg-blue-100 text-blue-600 w-10 h-10 rounded-lg flex items-center justify-center shrink-0 font-bold">B</div>
                      <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <Target size={20} className="text-gray-400"/> Kişisel Verilerin Hangi Amaçla İşleneceği
                          </h3>
                          <p className="text-gray-600 leading-relaxed text-sm">
                            Şirketimiz ile ilişkiniz dolayısıyla elde edilen kişisel verileriniz; faaliyetlerin mevzuata uygun yürütülmesi, satış süreçleri, müşteri memnuniyeti, pazarlama, analiz, faturalandırma, yasal yükümlülüklerin yerine getirilmesi, kişiye özel antrenman programlarının hazırlanması ve sözleşmelerin ifası gibi amaçlarla işlenmektedir.
                          </p>
                      </div>
                  </div>
              </div>

              {/* C) Aktarım */}
              <div className="p-8 md:p-12 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <div className="flex gap-4">
                      <div className="bg-blue-100 text-blue-600 w-10 h-10 rounded-lg flex items-center justify-center shrink-0 font-bold">C</div>
                      <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <Share2 size={20} className="text-gray-400"/> İşlenen Verilerin Aktarımı
                          </h3>
                          <p className="text-gray-600 leading-relaxed text-sm">
                            Verileriniz; kanuni yükümlülükler ve yukarıda belirtilen amaçlar doğrultusunda iş ortaklarımızla, yetkili kamu kurumlarıyla ve hizmet sağlayıcılarla, KVKK’nın 8. ve 9. maddelerinde belirtilen şartlar dahilinde paylaşılabilecektir.
                          </p>
                      </div>
                  </div>
              </div>

              {/* D) Yöntem ve Sebepler */}
              <div className="p-8 md:p-12 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <div className="flex gap-4">
                      <div className="bg-blue-100 text-blue-600 w-10 h-10 rounded-lg flex items-center justify-center shrink-0 font-bold">D</div>
                      <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <Database size={20} className="text-gray-400"/> Toplama Yöntemleri ve Hukuki Sebepler
                          </h3>
                          <p className="text-gray-600 leading-relaxed mb-4 text-sm">
                            Verileriniz; mobil uygulama, web sitesi, e-posta, çağrı merkezi gibi kanallarla otomatik ya da otomatik olmayan yollarla toplanır.
                          </p>
                          <div className="bg-white p-4 rounded-xl border border-gray-200">
                              <h5 className="text-sm font-bold text-gray-900 mb-2">Hukuki Sebepler:</h5>
                              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                                  <li>Kanunlarda açıkça öngörülmesi</li>
                                  <li>Sözleşmenin kurulması veya ifası</li>
                                  <li>Hukuki yükümlülüğün yerine getirilmesi</li>
                                  <li>Veri sorumlusunun meşru menfaatleri</li>
                                  <li>İlgili kişinin açık rızası</li>
                              </ul>
                          </div>
                      </div>
                  </div>
              </div>

              {/* E) Haklar */}
              <div className="p-8 md:p-12 border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <div className="flex gap-4">
                      <div className="bg-blue-100 text-blue-600 w-10 h-10 rounded-lg flex items-center justify-center shrink-0 font-bold">E</div>
                      <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                              <Lock size={20} className="text-gray-400"/> İlgili Kişinin Hakları (KVKK Md. 11)
                          </h3>
                          <p className="text-gray-600 leading-relaxed text-sm mb-4">
                            Herkes, veri sorumlusuna başvurarak kendisiyle ilgili aşağıdaki hakları kullanabilir:
                          </p>
                          <ul className="grid md:grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-600 list-disc list-inside">
                              <li>Veri işlenip işlenmediğini öğrenme</li>
                              <li>İşlenmişse buna ilişkin bilgi talep etme</li>
                              <li>İşlenme amacını ve amacına uygunluğu öğrenme</li>
                              <li>Yurt içi/dışı aktarılan kişileri bilme</li>
                              <li>Eksik/yanlış işlemelerin düzeltilmesini isteme</li>
                              <li>Silinmesini veya yok edilmesini isteme</li>
                              <li>Otomatik analiz sonuçlarına itiraz etme</li>
                              <li>Zararın giderilmesini talep etme</li>
                          </ul>
                      </div>
                  </div>
              </div>

          </div>

          {/* F) İLETİŞİM VE BAŞVURU */}
          <div className="bg-primary-900 p-10 md:p-14 text-center text-white">
             <div className="inline-flex items-center justify-center w-14 h-14 bg-white/10 rounded-2xl mb-6">
                <FileText size={28} />
             </div>
             <h3 className="text-2xl font-bold mb-4">Haklarınızı Kullanmak İçin</h3>
             <p className="text-primary-100 mb-8 max-w-2xl mx-auto">
               Yasal haklarınıza yönelik başvurularınızı ve taleplerinizi, hazırladığımız başvuru formu üzerinden şirketimize kolayca iletebilirsiniz. Talepleriniz en geç 30 gün içinde sonuçlandırılacaktır.
             </p>
             
             <Link 
               to="/kvkk/basvuru" 
               className="inline-flex items-center gap-2 bg-white text-primary-900 px-8 py-4 rounded-xl font-bold hover:bg-primary-50 transition-all shadow-lg hover:shadow-xl hover:-translate-y-1"
             >
               Başvuru Formunu Doldur <ArrowRight size={20} />
             </Link>
          </div>

        </motion.div>
      </Section>
    </>
  );
}

export default KVKK;